import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import sharepointService from '../services/sharepointService';

import { documentService } from '../services/documentService';

const SharePointContext = createContext({});

export const useSharePoint = () => {
  const context = useContext(SharePointContext);
  if (!context) {
    throw new Error('useSharePoint must be used within SharePointProvider');
  }
  return context;
};

export const SharePointProvider = ({ children }) => {
  const { user } = useAuth();
  const [isConfigured, setIsConfigured] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const [availableDrives, setAvailableDrives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load available SharePoint sites via Edge Function
  const loadSharePointSites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[SHAREPOINT] Loading SharePoint sites via Edge Function...');
      
      // Call Edge Function which uses application-only authentication
      const sites = await sharepointService?.getUserSites();
      
      console.log('[SHAREPOINT] Retrieved sites:', sites?.length);
      setAvailableSites(sites || []);
      
      // Auto-select first site if available
      if (sites?.length > 0 && !selectedSite) {
        console.log('[SHAREPOINT] Auto-selecting first site:', sites?.[0]?.displayName);
        await selectSite(sites?.[0]);
      }
    } catch (err) {
      console.error('[SHAREPOINT] Error loading SharePoint sites:', err);
      
      // Provide helpful error messages
      let errorMessage = err?.message || 'Failed to load SharePoint sites';
      
      if (errorMessage?.includes('credentials not configured')) {
        errorMessage = 'Microsoft Graph credentials not configured. Please configure them in System Settings → Integrations.';
      } else if (errorMessage?.includes('Unauthorized')) {
        errorMessage = 'Authentication failed. Please ensure you are logged in.';
      } else if (errorMessage?.includes('Token acquisition failed')) {
        errorMessage = 'Microsoft authentication failed. Please check your credentials in System Settings.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Select a SharePoint site
  const selectSite = async (site) => {
    try {
      setLoading(true);
      setSelectedSite(site);
      sharepointService?.setSiteId(site?.id);
      
      // Load document libraries for this site via Edge Function
      const drives = await sharepointService?.getDocumentLibraries(site?.id);
      setAvailableDrives(drives || []);
      
      // Auto-select first drive if available
      if (drives?.length > 0) {
        selectDrive(drives?.[0]);
      }
    } catch (err) {
      console.error('Error selecting site:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Select a document library (drive)
  const selectDrive = (drive) => {
    setSelectedDrive(drive);
    sharepointService?.setDriveId(drive?.id);
    
    // Mark as configured when site and drive are selected
    if (selectedSite && drive) {
      setIsConfigured(true);
      // Switch document service to SharePoint storage
      documentService?.setStorageStrategy('sharepoint');
      console.log('[SHAREPOINT] Storage strategy switched to SharePoint');
      
      // Persist SharePoint configuration to localStorage
      try {
        localStorage.setItem('zentri_sharepoint_configured', 'true');
        localStorage.setItem('zentri_sharepoint_site', JSON.stringify(selectedSite));
        localStorage.setItem('zentri_sharepoint_drive', JSON.stringify(drive));
      } catch (err) {
        console.warn('[SHAREPOINT] Could not persist configuration to localStorage:', err);
      }
    }
  };

  // Reset configuration
  const resetConfiguration = () => {
    setSelectedSite(null);
    setSelectedDrive(null);
    setIsConfigured(false);
    sharepointService?.setSiteId(null);
    sharepointService?.setDriveId(null);
    // Switch back to Supabase storage when SharePoint is deactivated
    documentService?.setStorageStrategy('supabase');
    console.log('[SHAREPOINT] Storage strategy switched back to Supabase');
    
    // Clear persisted SharePoint configuration
    try {
      localStorage.removeItem('zentri_sharepoint_configured');
      localStorage.removeItem('zentri_sharepoint_site');
      localStorage.removeItem('zentri_sharepoint_drive');
      localStorage.removeItem('zentri_storage_strategy');
    } catch (err) {
      console.warn('[SHAREPOINT] Could not clear persisted configuration:', err);
    }
  };

  // FIXED: Removed delegated authentication - Edge Function handles all authentication
  // This function now just loads SharePoint sites using application-only authentication
  const authenticateWithMicrosoft = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load SharePoint sites via Edge Function
      // No user consent popup needed - Edge Function uses client credentials
      await loadSharePointSites();
      
      console.log('[SHAREPOINT] Connected to SharePoint using application-only authentication');
    } catch (err) {
      console.error('Error connecting to SharePoint:', err);
      setError(err?.message || 'Failed to connect to SharePoint');
    } finally {
      setLoading(false);
    }
  };

  // Restore SharePoint configuration from localStorage on mount
  useEffect(() => {
    if (user) {
      try {
        const configured = localStorage.getItem('zentri_sharepoint_configured');
        const siteStr = localStorage.getItem('zentri_sharepoint_site');
        const driveStr = localStorage.getItem('zentri_sharepoint_drive');
        
        if (configured === 'true' && siteStr && driveStr) {
          const site = JSON.parse(siteStr);
          const drive = JSON.parse(driveStr);
          
          console.log('[SHAREPOINT] Restoring configuration from localStorage');
          setSelectedSite(site);
          setSelectedDrive(drive);
          setIsConfigured(true);
          
          sharepointService?.setSiteId(site?.id);
          sharepointService?.setDriveId(drive?.id);
          documentService?.setStorageStrategy('sharepoint');
        }
      } catch (err) {
        console.warn('[SHAREPOINT] Could not restore configuration from localStorage:', err);
      }
    }
  }, [user]);

  const value = {
    isConfigured,
    selectedSite,
    selectedDrive,
    availableSites,
    availableDrives,
    loading,
    error,
    microsoftToken: true, // Always true since Edge Function handles auth
    selectSite,
    selectDrive,
    resetConfiguration,
    authenticateWithMicrosoft,
    loadSharePointSites
  };

  return (
    <SharePointContext.Provider value={value}>
      {children}
    </SharePointContext.Provider>
  );
};